import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ValueProposition: React.FC = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section id="about" className="section bg-white">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.h2 
            variants={itemVariants} 
            className="text-3xl md:text-4xl font-bold mb-6 text-neutral-dark"
          >
            Uma Abordagem Multidisciplinar para o Design
          </motion.h2>

          <motion.p 
            variants={itemVariants}
            className="text-lg md:text-xl text-neutral-dark/80 mb-8"
          >
            Combinando expertise em design e visual merchandising para criar soluções simples que transformam espaços e impulsionam negócios.
          </motion.p>

          {/* Foto da Raquel em badge dourado */}
          <motion.div 
            variants={itemVariants}
            className="flex justify-center mb-10"
          >
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-yellow-400 border-4 border-yellow-500 shadow-lg flex items-center justify-center overflow-hidden">
              <img 
                src="/images/raquel.jpeg" 
                alt="Raquel de Araújo" 
                className="w-full h-full object-cover rounded-full"
                loading="lazy"
              />
            </div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <motion.div variants={itemVariants} className="bg-neutral-light p-6 rounded-lg shadow-sm">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                  <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Experiência Internacional</h3>
              <p className="text-neutral-dark/70">Participação em eventos globais como NRF New York e formação em Barcelona</p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-neutral-light p-6 rounded-lg shadow-sm">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Formação Acadêmica</h3>
              <p className="text-neutral-dark/70">Engenharia Civil, Especialização em Design de Interiores e Visual Merchandising, Mestrado em Comunicação e Doutoranda em Gestão Ambiental</p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-neutral-light p-6 rounded-lg shadow-sm">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z" />
                  <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z" />
                  <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Visão Estratégica</h3>
              <p className="text-neutral-dark/70">Integração de design, funcionalidade e estratégia de negócios para criar soluções que impulsionam vendas e fidelizam clientes</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ValueProposition; 